import { DocumentUrlsResult } from 'shared/domain/document/documentUrlsModel';
import { DocumentUrlsModel } from 'shared/domain/document/documentUrlsModel';
import { mergeDocumentUrlsModel } from 'shared/domain/document/mergeDocumentUrlsModel';
import { DisplayedFile, DisplayedFilesStore } from '../gallery/filesStore';
import {
  isSavedFileType,
  isUnsavedFileType,
  FileReadSuccess,
} from './types';
import {
  documentPreviewService,
  fileKindService,
  signedRequestService,
} from 'services/fileService';
import { documentService } from 'services/documentService';
import {
  resolveUnsavedFileForDisplay,
  markFileAsNotLoading,
  setFileSignedRequest,
} from 'store/document/documentFileStateMutations';
import {
  cacheResolvedPreviewState,
  clearDocumentUrlsRequestCache,
  getSharedDocumentUrlsRequestCache,
  markPdfUrlRetryAttempt,
  setSharedDocumentUrlsRequestCacheEntry,
  shouldSkipPdfUrlRetry,
} from './previewUrlsCache';

function hasPdfConversionPreviewUrls(urls: DocumentUrlsModel): boolean {
  return Boolean(urls.imageSignedRequest || urls.thumbnailSignedRequest);
}

function canResolveUnsavedPreview(
  file: DisplayedFile<FileReadSuccess | object>
): file is DisplayedFile<FileReadSuccess> {
  return typeof file.src === 'string';
}

/**
 * Applies resolved document URLs to the gallery store (URLs only).
 * imageConvertStatus on DisplayedFile comes from issue sync, not getSources.
 */
export async function applyDocumentUrlsResultToDisplayedFile(
  file: DisplayedFile<FileReadSuccess | object>,
  response: DocumentUrlsResult,
  filesStore: DisplayedFilesStore,
  fileIdSelector: (
    file: DisplayedFile<FileReadSuccess | object>
  ) => number | undefined,
  options?: { forceApply?: boolean }
): Promise<void> {
  const fileId = fileIdSelector(file);
  if (fileId === undefined) {
    return;
  }

  const existingFile = filesStore
    .get()
    .find((entry) => fileIdSelector(entry) === fileId);
  if (!existingFile) {
    return;
  }

  const mergedDocumentUrls = mergeDocumentUrlsModel(
    existingFile.signedRequest,
    response.documentUrls
  );
  const mergedResponse: DocumentUrlsResult = {
    ...response,
    documentUrls: mergedDocumentUrls,
  };

  const shouldUpdateSignedRequest = signedRequestService.shouldUpdateSignedRequest(
    existingFile,
    mergedResponse
  );
  const shouldUpdateLoadingState = existingFile.loading === true;

  if (
    !options?.forceApply &&
    !shouldUpdateSignedRequest &&
    !shouldUpdateLoadingState
  ) {
    return;
  }

  const {
    src: originalSrc,
    downloadSrc,
    thumbnail,
  } = await documentPreviewService.resolveDocumentPreviewSources({
    fileForDownload: file,
    existingFile,
    response: mergedResponse,
  });

  setFileSignedRequest({
    fileId,
    filesStore,
    fileSelector: fileIdSelector,
    thumbnail,
    downloadSrc,
    src: originalSrc,
    signedRequest: mergedDocumentUrls,
  });

  cacheResolvedPreviewState(existingFile, {
    signedRequest: mergedDocumentUrls,
    downloadSrc,
    thumbnail,
    src: originalSrc,
  });
}

/**
 * Creates a download function for resolving file thumbnails and sources.
 * Matches production (991b4ffbb) fetch/cache flow; status stays on issue sync.
 */
export function makeDownloadFile(
  fileIdSelector: (
    file: DisplayedFile<FileReadSuccess | object>
  ) => number | undefined,
  _cache: unknown,
  filesStore: DisplayedFilesStore
): (file: DisplayedFile<FileReadSuccess | object>) => void {
  const cache = getSharedDocumentUrlsRequestCache();

  return function downloadFile(
    file: DisplayedFile<FileReadSuccess | object>
  ): void {
    const fileId = fileIdSelector(file);

    if (isUnsavedFileType(file)) {
      if (canResolveUnsavedPreview(file)) {
        return resolveUnsavedFileForDisplay(file, filesStore);
      }
      markFileAsNotLoading(file, filesStore);
      return;
    }

    if (!isSavedFileType(file)) {
      markFileAsNotLoading(file, filesStore);
      return;
    }

    if (fileId === undefined) {
      markFileAsNotLoading(file, filesStore);
      return;
    }

    if (fileId === -1) {
      return;
    }

    const isPdfFile = fileKindService.isPdf(file);
    const cachedRequest = cache.get(fileId);

    if (cachedRequest) {
      if (isPdfFile) {
        void cachedRequest
          .then(async (cachedResponse: DocumentUrlsResult) => {
            if (!cachedResponse.documentUrls.imageSignedRequest) {
              if (shouldSkipPdfUrlRetry(fileId)) {
                await applyDocumentUrlsResultToDisplayedFile(
                  file,
                  cachedResponse,
                  filesStore,
                  fileIdSelector
                );
                return;
              }

              try {
                markPdfUrlRetryAttempt(fileId);
                const freshResponse =
                  await documentService.resolveDocumentUrlsWithRetry(fileId);
                await applyDocumentUrlsResultToDisplayedFile(
                  file,
                  freshResponse,
                  filesStore,
                  fileIdSelector
                );
                if (hasPdfConversionPreviewUrls(freshResponse.documentUrls)) {
                  clearDocumentUrlsRequestCache(fileId);
                }
              } catch {
                markFileAsNotLoading(file, filesStore, fileIdSelector);
              }
            } else {
              await applyDocumentUrlsResultToDisplayedFile(
                file,
                cachedResponse,
                filesStore,
                fileIdSelector
              );
              if (hasPdfConversionPreviewUrls(cachedResponse.documentUrls)) {
                clearDocumentUrlsRequestCache(fileId);
              }
            }
          })
          .catch(() => {
            markFileAsNotLoading(file, filesStore, fileIdSelector);
          });
        return;
      }

      void cachedRequest
        .then(async (cachedResponse: DocumentUrlsResult) => {
          await applyDocumentUrlsResultToDisplayedFile(
            file,
            cachedResponse,
            filesStore,
            fileIdSelector
          );
          clearDocumentUrlsRequestCache(fileId);
        })
        .catch(() => {
          markFileAsNotLoading(file, filesStore, fileIdSelector);
        });
      return;
    }

    const fetchPromise = documentService.resolveDocumentUrlsWithRetry(fileId);
    setSharedDocumentUrlsRequestCacheEntry(fileId, fetchPromise);

    void fetchPromise
      .then(async (response: DocumentUrlsResult) => {
        await applyDocumentUrlsResultToDisplayedFile(
          file,
          response,
          filesStore,
          fileIdSelector
        );
        if (!isPdfFile || hasPdfConversionPreviewUrls(response.documentUrls)) {
          clearDocumentUrlsRequestCache(fileId);
        }
      })
      .catch(() => {
        markFileAsNotLoading(file, filesStore, fileIdSelector);
      });
  };
}
